import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * The search behind an entity picker, single or multiple.
 *
 * Extracted rather than duplicated. The audit that produced these components
 * argued for one component with a `multiple` prop, and the shells did turn out
 * to differ more than a flag: one closes on choose and shows a value, the
 * other stays open and shows chips, and their `value` types are `T | null`
 * against `T[]`. Forcing both through one prop signature produced worse types
 * than two shells over one hook.
 *
 * What must not be duplicated is this — the debounce, the stale-response
 * guard, the paging and the four states. Those are where a second, subtly
 * different implementation would go wrong, and where a fix would land in one
 * picker and not the other.
 */
export interface EntitySearchPage<T> {
  items: T[];
  nextCursor: string | null;
  total?: number;
}

export interface UseEntitySearch<T> {
  search: string;
  setSearch: (s: string) => void;
  items: T[];
  cursor: string | null;
  total?: number;
  loading: boolean;
  error: string | null;
  /** Below `minChars`, so nothing has been requested. */
  tooShort: boolean;
  /** Long enough that a request has been or will be made. */
  ready: boolean;
  /** Nothing typed: the list is the first few rows, not a search. */
  browsing: boolean;
  /** Browsing, and there is more than is shown — say so. */
  truncated: boolean;
  loadMore: () => void;
  retry: () => void;
  reset: () => void;
}

export function useEntitySearch<T>(opts: {
  fetchOptions: (search: string, cursor: string | null) => Promise<EntitySearchPage<T>>;
  active: boolean;
  /**
   * Below this many characters nothing is requested — EXCEPT the empty
   * query. Opening a picker with nothing typed asks for the first few rows
   * (see `initial`), so a person is never shown a blank list when data
   * exists. Set `initial` to 0 to restore the old blank-until-typed rule.
   */
  minChars: number;
  debounceMs: number;
  /** Re-runs when these change — the parent of a cascade. */
  deps: unknown[];
  /** How many rows an empty query shows. Default 5. */
  initial?: number;
}): UseEntitySearch<T> {
  const { fetchOptions, active, minChars, debounceMs, deps, initial = 5 } = opts;

  const [search, setSearch] = useState('');
  const [items, setItems] = useState<T[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [total, setTotal] = useState<number | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Guards against an older response landing after a newer one.
   *
   * The API client has no abort signal, so a slow request for "ra" can arrive
   * after a fast one for "rahul" and repaint the list with the wrong rows —
   * which reads as the search being broken rather than as a race. Every
   * request carries a sequence number and only the newest may write.
   */
  const seq = useRef(0);

  const trimmed = search.trim();
  const tooShort = trimmed.length > 0 && trimmed.length < minChars;
  /** True when the list is the "top few" for an empty query. */
  const browsing = trimmed.length === 0 && initial > 0;
  const ready = trimmed.length >= minChars || browsing;

  const load = useCallback(async (term: string, from: string | null) => {
    const mine = ++seq.current;
    setLoading(true);
    setError(null);
    try {
      const page = await fetchOptions(term, from);
      if (mine !== seq.current) return;
      setItems((prev) => (from ? [...prev, ...page.items] : page.items));
      setCursor(page.nextCursor);
      setTotal(page.total);
    } catch (err) {
      if (mine !== seq.current) return;
      setError(err instanceof Error ? err.message : 'Unable to load results.');
    } finally {
      if (mine === seq.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchOptions, ...deps]);

  // The debounce is the only backpressure there is: the client cannot cancel
  // a request in flight, so not firing one is the cheapest way to avoid a
  // request per keystroke.
  useEffect(() => {
    if (!active) return undefined;
    if (!ready) {
      setItems([]);
      setCursor(null);
      setTotal(undefined);
      return undefined;
    }
    // The empty query is not debounced: it fires the moment the picker
    // opens, because there is nothing to wait for the user to finish typing.
    const t = setTimeout(() => load(trimmed, null), trimmed ? debounceMs : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trimmed, active, ready, debounceMs, load]);

  const reset = useCallback(() => {
    setSearch('');
    setItems([]);
    setCursor(null);
    setTotal(undefined);
    setError(null);
  }, []);

  // An empty query shows the top few only. The page the server returned may
  // be longer; the rest is reachable by typing, which is what the footer says.
  const shown = browsing ? items.slice(0, initial) : items;

  return {
    search,
    setSearch,
    items: shown,
    cursor,
    total,
    loading,
    error,
    tooShort,
    ready,
    browsing,
    truncated: browsing && (items.length > initial || Boolean(cursor)),
    loadMore: () => { if (cursor) load(trimmed, cursor); },
    retry: () => load(trimmed, null),
    reset,
  };
}

/** Shared focus/dismiss behaviour for a popover that owns a search field. */
export function useDismissable(open: boolean, close: () => void) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    function onDown(e: MouseEvent) {
      if (!ref.current?.contains(e.target as Node)) close();
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') { e.stopPropagation(); close(); }
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [open, close]);

  return ref;
}
